import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Tokyo" });
  } catch { return "—"; }
}

function durationMin(record) {
  if (!record?.clock_in?.at || !record?.clock_out?.at) return 0;
  const inTs = new Date(record.clock_in.at).getTime();
  const outTs = new Date(record.clock_out.at).getTime();
  let breakMs = 0;
  for (const b of record.breaks || []) {
    if (b.start?.at && b.end?.at) breakMs += new Date(b.end.at) - new Date(b.start.at);
  }
  return Math.max(0, Math.round((outTs - inTs - breakMs) / 60000));
}

function fmtHM(mins) {
  const h = Math.floor(mins / 60); const m = mins % 60;
  return `${h}時間${m.toString().padStart(2, "0")}分`;
}

export default function MonthlyView() {
  const { slug } = useParams();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/attendance/monthly`, { params: { year, month } })
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, [year, month]);

  const totalMin = items.reduce((s, r) => s + durationMin(r), 0);
  const workedDays = items.filter((r) => r.clock_in).length;

  const prev = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const next = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <header className="bg-white border-b border-stone-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to={`/t/${slug}/`} data-testid="back-dashboard-link" className="inline-flex items-center gap-1 text-stone-500 hover:text-stone-900 text-sm"><ChevronLeft size={16} /> 打刻に戻る</Link>
          <div className="font-display text-lg font-bold text-stone-900 flex items-center gap-2"><Calendar size={18} /> 月次打刻</div>
          <span className="w-20" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <button onClick={prev} data-testid="prev-month-btn" className="rounded-full p-2 bg-white border border-stone-200 hover:bg-stone-50"><ChevronLeft size={18} /></button>
            <div className="font-display text-2xl font-black text-stone-900" data-testid="ym-label">{year}年 {month}月</div>
            <button onClick={next} data-testid="next-month-btn" className="rounded-full p-2 bg-white border border-stone-200 hover:bg-stone-50"><ChevronRight size={18} /></button>
          </div>
          <div className="flex items-center gap-4">
            <Stat label="勤務日" value={`${workedDays}日`} />
            <Stat label="実労働" value={fmtHM(totalMin)} />
          </div>
        </div>

        {/* List only */}
        <div className="card-soft p-4 md:p-6">
          <div className="font-display text-lg font-bold text-stone-900 mb-3">打刻一覧</div>
          {loading ? <div className="text-stone-500 text-sm">読み込み中...</div> : items.length === 0 ? (
            <div className="text-stone-500 text-sm">この月の打刻はまだありません。</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-stone-500">
                  <tr><th className="text-left py-2 px-2">日付</th><th className="text-left">出勤</th><th className="text-left">退勤</th><th className="text-left">休憩</th><th className="text-left">実労働</th></tr>
                </thead>
                <tbody>
                  {items.map((r) => {
                    const mins = durationMin(r);
                    const breakCnt = (r.breaks || []).length;
                    return (
                      <tr key={r.date} className="border-t border-stone-100" data-testid={`row-${r.date}`}>
                        <td className="py-2 px-2 font-semibold">{r.date}</td>
                        <td>{fmtTime(r.clock_in?.at)}</td>
                        <td>{fmtTime(r.clock_out?.at)}</td>
                        <td>{breakCnt > 0 ? `${breakCnt}回` : "—"}</td>
                        <td>{mins ? fmtHM(mins) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-right">
      <div className="text-xs text-stone-500">{label}</div>
      <div className="font-display font-black text-stone-900">{value}</div>
    </div>
  );
}
